import ActivitySector from "@/interfaces/ActivitySector";
import CompanyCourse, {
  CompaniesCoursesFiltersSend,
  CompanyCourseFilters,
  CompanyCoursePeopleFilters,
} from "@/interfaces/CompanyCourse";
import CompanyCourseStatus from "@/interfaces/CompanyCourseStatus";
import CompanyStatus, {
  CompanyStatusFilters,
} from "@/interfaces/CompanyStatus";
import Company, { CompanyFiltersSend } from "@/interfaces/Company";
import Course, { CourseFilters } from "@/interfaces/Course";
import EmailTemplate from "@/interfaces/EmailTemplate";
import { IFilters, IPaginateResponse } from "@/interfaces/Fetch";
import User, { UserFilters } from "@/interfaces/IUser";
import Instructor, { InstructorFilters } from "@/interfaces/Instructor";
import MontageMeetingStatus from "@/interfaces/MontageMeetingStatus";
import Opco from "@/interfaces/Opco";
import Organisation from "@/interfaces/Organisation";
import Person from "@/interfaces/Person";
import TrainingBrochure, {
  TrainingBrochureFilters,
} from "@/interfaces/TrainingBrochure";
import {
  GetActivitySectorsParams,
  useLazyGetActivitySectorsQuery,
} from "@/services/activitySectors";
import { useLazyGetCompaniesQuery } from "@/services/companies";
import {
  useLazyGetCompaniesCoursesQuery,
  useLazyGetCompanyCoursesQuery,
} from "@/services/companies/courses";
import { useLazyGetCompanyCourseStatusesQuery } from "@/services/companies/courses/statuses";
import { useLazyGetCompanyPeopleQuery } from "@/services/companies/people";
import { useLazyGetCompanyStatusesQuery } from "@/services/companies/statuses";
import { useLazyGetCoursesQuery } from "@/services/courses";
import { useLazyGetMontageMeetingStatusesQuery } from "@/services/montageMeetingStatuses";
import { useLazyGetEmailTemplatesQuery } from "@/services/emailTemplates";
import { useLazyGetInstructorsQuery } from "@/services/instructors";
import { useLazyGetOpcosQuery } from "@/services/opcos";
import { useLazyGetOrganisationsQuery } from "@/services/organisations";
import { useLazyGetTrainingBrochuresQuery } from "@/services/trainingBrochures";
import {
  useLazyGetCalendarFilterableQuery,
  useLazyGetUsersQuery,
} from "@/services/users";
import { GroupBase, OptionsOrGroups } from "react-select";

type IProps =
  | {
      onCallback?: (name: string, response: IPaginateResponse<any>) => void;
    }
  | undefined;

const useLoadOptions = ({ onCallback }: IProps = {}) => {
  const [getUsersLazy] = useLazyGetUsersQuery();
  const [getCalendarFilterableLazy] = useLazyGetCalendarFilterableQuery();
  const [getCompanyStatusesLazy] = useLazyGetCompanyStatusesQuery();
  const [getMontageMeetingStatusesLazy] =
    useLazyGetMontageMeetingStatusesQuery();
  const [getOpcosLazy] = useLazyGetOpcosQuery();
  const [getCoursesLazy] = useLazyGetCoursesQuery();
  const [getCompanyCourseStatusesLazy] = useLazyGetCompanyCourseStatusesQuery();
  const [getOrganisationsLazy] = useLazyGetOrganisationsQuery();
  const [getInstructorsLazy] = useLazyGetInstructorsQuery();
  const [getCompanyPeopleLazy] = useLazyGetCompanyPeopleQuery();
  const [getCompanyCoursesLazy] = useLazyGetCompanyCoursesQuery();
  const [getCompaniesCoursesLazy] = useLazyGetCompaniesCoursesQuery();
  const [getCompaniesLazy] = useLazyGetCompaniesQuery();
  const [getTrainingBrochuresLazy] = useLazyGetTrainingBrochuresQuery();
  const [getEmailTemplatesLazy] = useLazyGetEmailTemplatesQuery();
  const [getActivitySectorsLazy] = useLazyGetActivitySectorsQuery();

  const loadCompanyCoursesOptions = async (
    query: string,
    _: OptionsOrGroups<CompanyCourse, GroupBase<CompanyCourse>>,
    filters?: CompanyCourseFilters,
  ) => {
    const response = await getCompanyCoursesLazy([
      filters!.companyId!,
      {
        query,
        ...filters!,
      },
    ]).unwrap();
    onCallback?.("loadCompanyCoursesOptions", response);
    return {
      options: response.data,
      hasMore: response.meta.currentPage !== response.meta.lastPage,
      additional: {
        ...filters,
        pageIndex: response.meta.currentPage,
      },
    };
  };

  const loadTrainingBrochureOptions = async (
    query: string,
    _: OptionsOrGroups<TrainingBrochure, GroupBase<TrainingBrochure>>,
    filters?: TrainingBrochureFilters,
  ) => {
    const response = await getTrainingBrochuresLazy(filters).unwrap();
    onCallback?.("loadTrainingBrochureOptions", response);
    return {
      options: response.data,
      hasMore: response.meta.currentPage !== response.meta.lastPage,
      additional: {
        ...filters,
        pageIndex: response.meta.currentPage,
      },
    };
  };

  const loadEmailTemplatesOptions = async (
    _: string,
    __: OptionsOrGroups<EmailTemplate, GroupBase<EmailTemplate>>,
    filters?: IFilters,
  ) => {
    const response = await getEmailTemplatesLazy().unwrap();
    onCallback?.("loadEmailTemplatesOptions", response);
    return {
      options: response.data,
      hasMore: response.meta.currentPage !== response.meta.lastPage,
      additional: {
        ...filters,
        pageIndex: response.meta.currentPage,
      },
    };
  };

  const loadActivitySectors = async (
    query: string,
    __: OptionsOrGroups<ActivitySector, GroupBase<ActivitySector>>,
    filters?: GetActivitySectorsParams,
  ) => {
    const response = await getActivitySectorsLazy({
      query,
      ...filters,
    }).unwrap();
    onCallback?.("loadActivitySectors", response);
    return {
      options: response.data,
      hasMore: response.meta.currentPage !== response.meta.lastPage,
      additional: {
        ...filters,
        pageIndex: response.meta.currentPage,
      },
    };
  };

  const loadUsersOptions = async (
    query: string,
    _: OptionsOrGroups<User, GroupBase<User>>,
    filters?: UserFilters,
  ) => {
    const response = await getUsersLazy({
      query,
      ...filters!,
    }).unwrap();
    onCallback?.("loadUsersOptions", response);
    return {
      options: response.data,
      hasMore: response.meta.currentPage !== response.meta.lastPage,
      additional: {
        ...filters,
        pageIndex: response.meta.currentPage,
      },
    };
  };

  const loadCompaniesCoursesOptions = async (
    query: string,
    _: OptionsOrGroups<CompanyCourse, GroupBase<CompanyCourse>>,
    filters?: CompaniesCoursesFiltersSend,
  ) => {
    const response = await getCompaniesCoursesLazy({
      query,
      ...filters!,
    }).unwrap();
    onCallback?.("loadCompaniesCoursesOptions", response);
    return {
      options: response.data,
      hasMore: response.meta.currentPage !== response.meta.lastPage,
      additional: {
        ...filters,
        pageIndex: response.meta.currentPage,
      },
    };
  };

  const loadCompaniesOptions = async (
    query: string,
    _: OptionsOrGroups<Company, GroupBase<Company>>,
    filters?: CompanyFiltersSend,
  ) => {
    const response = await getCompaniesLazy({
      query,
      ...filters!,
    }).unwrap();
    onCallback?.("loadCompaniesOptions", response);
    return {
      options: response.data,
      hasMore: response.meta.currentPage !== response.meta.lastPage,
      additional: {
        ...filters,
        pageIndex: response.meta.currentPage,
      },
    };
  };

  const loadCompanyStatusesOptions = async (
    query: string,
    _: OptionsOrGroups<CompanyStatus, GroupBase<CompanyStatus>>,
    filters?: CompanyStatusFilters,
  ) => {
    const response = await getCompanyStatusesLazy({
      query,
      ...filters!,
    }).unwrap();
    onCallback?.("loadCompanyStatusesOptions", response);
    return {
      options: response.data,
      hasMore: response.meta.currentPage !== response.meta.lastPage,
      additional: {
        ...filters,
        pageIndex: response.meta.currentPage,
      },
    };
  };

  const loadMontageMeetingStatusesOptions = async (
    query: string,
    _: OptionsOrGroups<MontageMeetingStatus, GroupBase<MontageMeetingStatus>>,
    filters?: IFilters,
  ) => {
    const response = await getMontageMeetingStatusesLazy({
      query,
      ...filters!,
    }).unwrap();
    return {
      options: response.data,
      hasMore: response.meta.currentPage !== response.meta.lastPage,
      additional: {
        ...filters,
        pageIndex: response.meta.currentPage,
      },
    };
  };

  const loadOpcosOptions = async (
    query: string,
    _: OptionsOrGroups<Opco, GroupBase<Opco>>,
    filters?: IFilters,
  ) => {
    const response = await getOpcosLazy({
      query,
      ...filters,
    }).unwrap();
    onCallback?.("loadOpcosOptions", response);
    return {
      options: response.data,
      hasMore: response.meta.currentPage !== response.meta.lastPage,
      additional: {
        ...filters,
        pageIndex: response.meta.currentPage,
      },
    };
  };

  const loadCoursesOptions = async (
    query: string,
    _: OptionsOrGroups<Course, GroupBase<Course>>,
    filters?: CourseFilters,
  ) => {
    const response = await getCoursesLazy({
      query,
      archived: false,
      ...filters!,
    }).unwrap();
    onCallback?.("loadOptionsCourses", response);
    return {
      options: response.data,
      hasMore: response.meta.currentPage !== response.meta.lastPage,
      additional: {
        ...filters,
        pageIndex: response.meta.currentPage,
      },
    };
  };

  const loadCompanyCourseStatusesOptions = async (
    query: string,
    _: OptionsOrGroups<CompanyCourseStatus, GroupBase<CompanyCourseStatus>>,
    filters?: IFilters,
  ) => {
    const response = await getCompanyCourseStatusesLazy({
      query,
      ...filters!,
    }).unwrap();
    onCallback?.("loadCompanyCourseStatusesOptions", response);
    return {
      options: response.data,
      hasMore: response.meta.currentPage !== response.meta.lastPage,
      additional: {
        ...filters,
        pageIndex: response.meta.currentPage,
      },
    };
  };

  const loadOrganisationsOptions = async (
    query: string,
    _: OptionsOrGroups<Organisation, GroupBase<Organisation>>,
    filters?: IFilters,
  ) => {
    const response = await getOrganisationsLazy({
      query,
      ...filters,
    }).unwrap();
    onCallback?.("loadOrganizationsOptions", response);
    return {
      options: response.data,
      hasMore: response.meta.currentPage !== response.meta.lastPage,
      additional: {
        ...filters,
        pageIndex: response.meta.currentPage,
      },
    };
  };

  const loadInstructorsOptions = async (
    query: string,
    _: OptionsOrGroups<Instructor, GroupBase<Instructor>>,
    filters?: InstructorFilters,
  ) => {
    const response = await getInstructorsLazy({
      query,
      ...filters!,
    }).unwrap();
    onCallback?.("loadInstructorsOptions", response);
    return {
      options: response.data,
      hasMore: response.meta.currentPage !== response.meta.lastPage,
      additional: {
        ...filters,
        pageIndex: response.meta.currentPage,
      },
    };
  };

  const loadCompanyPeopleOptions = async (
    query: string,
    _: OptionsOrGroups<Person, GroupBase<Person>>,
    filters?: CompanyCoursePeopleFilters,
  ) => {
    const response = await getCompanyPeopleLazy([
      filters?.companyId!,
      {
        query,
        ...filters!,
      },
    ]).unwrap();
    onCallback?.("loadInstructorsOptions", response);
    return {
      options: response.data,
      hasMore: response.meta.currentPage !== response.meta.lastPage,
      additional: {
        ...filters,
        pageIndex: response.meta.currentPage,
      },
    };
  };

  const loadCalendarFilterableOptions = async (
    query: string,
    _: OptionsOrGroups<User, GroupBase<User>>,
    filters?: UserFilters,
  ) => {
    const response = await getCalendarFilterableLazy({
      query,
      ...(filters?.roles?.length && { roles: filters.roles }),
    }).unwrap();
    // Le pool n'est pas paginé et l'endpoint ignore isActive : filtré ici.
    return {
      options:
        filters?.isActive === undefined
          ? response
          : response.filter((user) => user.isActive === filters.isActive),
      hasMore: false,
    };
  };

  return {
    loadMontageMeetingStatusesOptions,
    loadUsersOptions,
    loadCalendarFilterableOptions,
    loadActivitySectors,
    loadCompanyStatusesOptions,
    loadOpcosOptions,
    loadCoursesOptions,
    loadCompanyCourseStatusesOptions,
    loadOrganisationsOptions,
    loadInstructorsOptions,
    loadCompanyPeopleOptions,
    loadCompanyCoursesOptions,
    loadCompaniesCoursesOptions,
    loadCompaniesOptions,
    loadTrainingBrochureOptions,
    loadEmailTemplatesOptions,
  };
};

export default useLoadOptions;
